import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ProfileDateFilterStateService } from '@core/services/profile-date-filter-state.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { CtaAnalyticsDialogComponent } from '@cta-analytics-dialog/cta-analytics-dialog.component';
import { ICtaAnalyticsDialogData } from '@cta-analytics-dialog/cta-analytics-dialog.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventViewMode } from '@enums/event-tracking.enum';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignDetailsDeliverablesComponent } from '@pages/campaigns/campaign-details/campaign-details-deliverables/campaign-details-deliverables.component';
import {
  ICampaignDetailsDeliverablesGroupedParentRow,
  ICampaignDetailsDeliverablesTableRow,
} from '@pages/campaigns/campaign-details/campaign-details-deliverables/campaign-details-deliverables.interface';
import { CampaignDetailsFilterService } from '@pages/campaigns/campaign-details/campaign-details-filter.service';
import { CampaignDetailsUndesignatedExecutionOrderService } from '@pages/campaigns/campaign-details/campaign-details-undesignated-execution-order.service';
import {
  ECampaignDetailsTab,
  ICampaignAnalyticsFilter,
} from '@pages/campaigns/campaign-details/campaign-details.interface';
import { ICampaignExportConfig } from '@pages/campaigns/campaign-details/campaign-export-config/campaign-export-config.interface';
import { CampaignExportConfigService } from '@pages/campaigns/campaign-details/campaign-export-config/campaign-export-config.service';
import { ICreator } from '@pages/creators/creators.interface';
import { ProfileCampaignFilterComponent } from '@shared/components/profile-campaign-filter/profile-campaign-filter.component';
import { ProfileDateFilterComponent } from '@shared/components/profile-date-filter/profile-date-filter.component';
import {
  EDateRangeType,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { calculatePredefinedDateRange } from '@shared/components/profile-date-filter/profile-date-filter.util';
import { ProfilePlatformFeatureSelectComponent } from '@shared/components/profile-platform-feature-select/profile-platform-feature-select.component';
import { TPlatform } from '@shared/components/social-badge-list/social-badge-list.interface';
import { ITileConfiguration } from '@shared/components/social-content-tile/social-content-tile.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { endOfDay, startOfDay } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  CreatorDetailsDeliverablesStore,
  ICreatorDetailsDeliverablesFilter,
} from './creator-details-deliverables.store';
import { widenDateRangeToRunningCampaigns } from './creator-details-deliverables.util';

/**
 * Custom equality for computed<Date | null> signals: compares by timestamp instead of object
 * reference, so a freshly-constructed Date representing the same instant as the previous value
 * doesn't register as a "changed" signal value. See effectiveStartDate/effectiveEndDate below.
 */
function dateOrNullEqual(a: Date | null, b: Date | null): boolean {
  if (a === b) {
    return true;
  }

  if (a === null || b === null) {
    return false;
  }

  return a.getTime() === b.getTime();
}

/**
 * The reverse of campaign-details' own Deliverables tab: one creator, all of their campaigns,
 * merged into a single table. Reuses CampaignDetailsDeliverablesComponent as-is (contextColumn
 * swapped to 'campaign', storageKeyPrefix namespaced to 'creator-details') for every filter/
 * status/group-toggle/row-building concern that's identical either way; this wrapper only owns
 * the new Scope section (date + platform + campaign - deliberately no creator filter, this page
 * is already scoped to one creator) and the data fetch/merge/dialog-opening logic that's unique
 * to spanning multiple campaigns.
 */
@Component({
  imports: [
    CampaignDetailsDeliverablesComponent,
    MatButtonModule,
    NgxSkeletonLoaderModule,
    ProfileCampaignFilterComponent,
    ProfileDateFilterComponent,
    ProfilePlatformFeatureSelectComponent,
    TranslateModule,
  ],
  providers: [CreatorDetailsDeliverablesStore],
  selector: 'app-creator-details-deliverables',
  templateUrl: './creator-details-deliverables.component.html',
})
export class CreatorDetailsDeliverablesComponent {
  public readonly adminMode = input<boolean>(false);
  public readonly creator = input<ICreator | null>(null);
  public readonly creatorId = input.required<string>();

  public readonly store = inject(CreatorDetailsDeliverablesStore);
  private readonly campaignExportConfigService = inject(CampaignExportConfigService);
  private readonly router = inject(Router);
  private readonly campaignDetailsFilterService = inject(CampaignDetailsFilterService);
  private readonly undesignatedExecutionOrderService = inject(CampaignDetailsUndesignatedExecutionOrderService);
  private readonly dateUtilities = inject(DateUtilitiesService);
  private readonly dialog = inject(MatDialog);
  private readonly profileDateFilterStateService = inject(ProfileDateFilterStateService);

  // Never in export mode - creator-details has no export/report concept - so this always yields
  // the fully-unrestricted default (see CampaignExportConfigService.calculateExportConfig(false, null)).
  public readonly campaignExportConfig = computed<ICampaignExportConfig>(() =>
    this.campaignExportConfigService.calculateExportConfig(false, null),
  );

  // The committed Scope selection - what actually drives scopeFilter (and therefore the fetch)
  // and effectiveAnalyticsNodes' client-side re-filter. Only ever updated once the corresponding
  // dropdown's panel actually closes (see onPlatformFeaturesPanelClosed/onCampaignIdsPanelClosed),
  // mirroring CampaignDetailsFiltersComponent's own local-model-vs-panelClosed-commit split, so a
  // fetch/refilter never fires mid-click while the user is still browsing the dropdown.
  public readonly selectedCampaignIds = signal<Set<string>>(new Set());
  public readonly selectedPlatformFeatures = signal<Set<EPlatformFeature>>(new Set());
  // The draft Scope selection - updated on every per-click change from the dropdowns themselves
  // (platformFeaturesChanged/campaignIdsChanged), fed back into their own `initialSelected...`
  // inputs so the displayed selection stays authoritative even before the panel closes.
  public readonly draftCampaignIds = signal<Set<string>>(new Set());
  public readonly draftPlatformFeatures = signal<Set<EPlatformFeature>>(new Set());
  // The range the picker shows. Requests use effectiveStartDate/effectiveEndDate below instead.
  public readonly startDate = signal<Date | null>(null);
  public readonly endDate = signal<Date | null>(null);
  public readonly selectedDateRangeType = signal<EDateRangeType>(EDateRangeType.last30Days);
  // The persisted selection (else "Last 30 days"), resolved once - the picker reads its
  // `initialState` only at ngOnInit, so it isn't rendered before this is set.
  public readonly dateFilterState = signal<IProfileDateFilterState | null>(null);

  // Unlike campaign-details (one campaign, so "Full period" is that campaign's own fixed
  // start/end), this page spans every campaign a creator is in - there's no single campaign to
  // scope to. Per explicit product decision, "Full period" here means "since this creator was
  // created, until now" rather than the min/max across their campaigns (that range is still used
  // for the platform-feature filter's own campaignAnalytics input, just not for date bounds).
  // `end: new Date()` is intentionally resolved once per `creator()` identity change (not
  // continuously live) - same one-shot-per-relevant-change semantics as every other predefined
  // range in ProfileDateFilterComponent (e.g. "This week" resolves `endOfDay(new Date())` once
  // per click, not continuously).
  public readonly fullPeriodBounds = computed<IDateRangeBounds | null>(() => {
    const createdAt = this.creator()?.createdAt;

    if (!createdAt) {
      return null;
    }

    // Normalized to day boundaries up front (startOfDay/endOfDay), exactly like
    // ProfileDateFilterComponent's own calculatePredefinedRange(allTime) normalizes whatever
    // bounds it's handed. Without this, the creator's exact createdAt time-of-day and the exact
    // instant this computed first ran leaked into the very first ("default", pre-emission) fetch,
    // while explicitly (re-)selecting "Full period" always went through that normalization -
    // so the untouched default and an explicit re-selection disagreed on bounds even though both
    // represent the same "Full period".
    return {
      end: endOfDay(new Date()),
      start: startOfDay(this.dateUtilities.parseDateTimeFromApi(createdAt)),
    };
  });

  // The picker's range widened to the in-scope campaigns running on its first or last day, so their
  // deliverables aren't cut off mid-campaign. Feeds only the requests and the planned-placeholder
  // filter. Compared by timestamp, so a picker emission (or a different range) that widens to the
  // same dates doesn't refetch.
  public readonly requestDateRange = computed<IDateRangeBounds | null>(() => {
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      return null;
    }

    const selectedCampaignIds = this.selectedCampaignIds();
    const scopedCampaigns = this.store
      .campaigns$()
      .filter((campaign) => !selectedCampaignIds.size || selectedCampaignIds.has(campaign.id));

    return widenDateRangeToRunningCampaigns({ end: endDate, start: startDate }, scopedCampaigns);
  });

  public readonly effectiveStartDate = computed<Date | null>(() => this.requestDateRange()?.start ?? null, {
    equal: dateOrNullEqual,
  });
  public readonly effectiveEndDate = computed<Date | null>(() => this.requestDateRange()?.end ?? null, {
    equal: dateOrNullEqual,
  });

  public readonly scopeFilter = computed<ICreatorDetailsDeliverablesFilter>(() => ({
    campaignIds: this.selectedCampaignIds(),
    endDate: this.effectiveEndDate(),
    platformFeatures: this.selectedPlatformFeatures(),
    startDate: this.effectiveStartDate(),
  }));

  // Same rule as CampaignDetailsDeliverablesComponent's own isContentLoading: store.loading$() only
  // turns true once the deliverables fetch starts, so without the null check the Scope filters
  // rendered as empty dropdowns while the campaigns list (or the creator) was still loading.
  public readonly scopeLoading = computed<boolean>(
    () => this.store.loading$() || this.store.analyticsNodes$() === null,
  );

  public readonly campaignNameById = computed<Map<string, string>>(
    () => new Map(Object.entries(this.store.campaignNameById$())),
  );

  public readonly campaignIconById = computed<Map<string, string | null | undefined>>(
    () => new Map(Object.entries(this.store.campaignIconById$())),
  );

  // Mirrors campaign-details.component.ts's own deliverablesAnalyticsNodes computed: the slices
  // HTTP call already applies date/platform params server-side, but shell-planned (not-yet-
  // executed) placeholder nodes are computed unconditionally from plannedCtas and need the same
  // client-side re-filtering here, plus the undesignated-execution-order enrichment that feeds
  // buildTableRow/getDateMetadata. No creatorIds/ctaTemplateIds/executionStatuses restriction at
  // this level - this page is already one creator, and cta-template/matching/status filtering
  // happens client-side inside CampaignDetailsDeliverablesComponent itself, same as campaign-details.
  public readonly effectiveAnalyticsNodes = computed(() => {
    const allAnalyticsNodes = this.store.analyticsNodes$();

    if (!allAnalyticsNodes) {
      return allAnalyticsNodes;
    }

    const filter: ICampaignAnalyticsFilter = {
      creatorIds: new Set(),
      ctaTemplateIds: new Set(),
      endDate: this.effectiveEndDate(),
      executionStatuses: new Set(),
      platformFeatures: this.selectedPlatformFeatures(),
      startDate: this.effectiveStartDate(),
    };

    const filteredNodes = this.campaignDetailsFilterService.filterShellPlannedAnalyticsNodes(allAnalyticsNodes, filter);

    return this.undesignatedExecutionOrderService.enrichAnalyticsNodesWithUndesignatedOrders(
      filteredNodes,
      allAnalyticsNodes,
    );
  });

  constructor() {
    this.initializeDateRangeEffect();

    // Independent of the fetchDeliverables effect below - deliberately not gated on `creator()`
    // resolving, since nothing here needs it, matching how creator-details.component.ts's own
    // (now-unused-by-this-tab) fetchCampaigns() only ever gated on creatorId.
    effect(() => {
      const creatorId = this.creatorId();

      if (!creatorId) {
        return;
      }

      this.store.fetchCreatorCampaigns({ adminMode: this.adminMode(), creatorId });
    });

    effect(() => {
      const creatorId = this.creatorId();
      const creator = this.creator();
      const campaigns = this.store.campaigns$();
      const campaignsError = this.store.campaignsError$();
      const campaignsLoading = this.store.campaignsLoading$();
      const filter = this.scopeFilter();

      // Mirrors campaign-details.component.ts's own gating on `campaignApi$()` before ever
      // calling setInitialDateRange()/store.updateFilter() - waiting for the creator, the campaigns
      // and the resolved date range means the very first fetch already sends the real, widened
      // range, instead of firing once with null dates and again moments later.
      if (!creatorId || !creator || campaignsLoading || campaignsError || !filter.startDate || !filter.endDate) {
        return;
      }

      this.store.fetchDeliverables({
        adminMode: this.adminMode(),
        campaigns,
        creator,
        creatorId,
        filter,
      });
    });
  }

  public onDateRangeChanged(dateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>): void {
    this.startDate.set(dateRange.startDate);
    this.endDate.set(dateRange.endDate);

    this.profileDateFilterStateService.persistState({
      endDate: dateRange.endDate,
      entityId: this.creatorId(),
      entityType: 'creatorDeliverables',
      selectedRange: this.selectedDateRangeType(),
      startDate: dateRange.startDate,
      userId: AuthService.currentDbUser?.id ?? null,
    });
  }

  public onSelectedRangeChanged(selectedRange: EDateRangeType): void {
    this.selectedDateRangeType.set(selectedRange);
  }

  // Per-click update - only the draft, exactly like CampaignDetailsFiltersComponent's own
  // onPlatformFeaturesChanged only touches its local model. The committed selection (and
  // therefore the fetch/refilter) only updates once the panel actually closes, below.
  public onPlatformFeaturesChanged(platformFeatures: Set<TPlatform>): void {
    this.draftPlatformFeatures.set(new Set(Array.from(platformFeatures) as EPlatformFeature[]));
  }

  public onCampaignIdsChanged(campaignIds: Set<string>): void {
    this.draftCampaignIds.set(campaignIds);
  }

  public onPlatformFeaturesPanelClosed(): void {
    this.selectedPlatformFeatures.set(this.draftPlatformFeatures());
  }

  public onPlatformFeaturesNormalized(platformFeatures: Set<TPlatform>): void {
    const normalizedPlatformFeatures = new Set(Array.from(platformFeatures) as EPlatformFeature[]);

    this.draftPlatformFeatures.set(normalizedPlatformFeatures);

    if (!this.setsAreEqual(this.selectedPlatformFeatures(), normalizedPlatformFeatures)) {
      this.selectedPlatformFeatures.set(normalizedPlatformFeatures);
    }
  }

  public onCampaignIdsNormalized(campaignIds: Set<string>): void {
    const normalizedCampaignIds = new Set(campaignIds);

    this.draftCampaignIds.set(normalizedCampaignIds);

    if (!this.setsAreEqual(this.selectedCampaignIds(), normalizedCampaignIds)) {
      this.selectedCampaignIds.set(normalizedCampaignIds);
    }
  }

  public onCampaignIdsPanelClosed(): void {
    this.selectedCampaignIds.set(this.draftCampaignIds());
  }

  public onRetryCampaigns(): void {
    const creatorId = this.creatorId();

    if (!creatorId) {
      return;
    }

    this.store.fetchCreatorCampaigns({ adminMode: this.adminMode(), creatorId });
  }

  public onDeliverableRowClicked(row: ICampaignDetailsDeliverablesTableRow): void {
    if (!this.campaignExportConfig().allowDeliverablesRowClick) {
      return;
    }

    this.openDialogForNode(row.analyticsNodeId, row.campaignId, {
      selectedCtaId: row.ctaTemplateId,
      selectedDeliverableId: row.deliverableId,
      selectedDeliverableType: row.deliverableType,
      viewMode: EEventViewMode.list,
    });
  }

  public onDeliverableRowGroupClicked(row: ICampaignDetailsDeliverablesGroupedParentRow): void {
    if (!this.campaignExportConfig().allowDeliverableGroupClick) {
      return;
    }

    this.openDialogForNode(row.analyticsNodeId, row.campaignId, {
      selectedCtaId: row.ctaTemplateId,
      viewMode: EEventViewMode.groupedByContent,
    });
  }

  // Opens the clicked row's campaign in a brand new tab, with this creator preselected in that
  // page's own Scope "Creator" filter - i.e. the campaign loads exactly as if the user had opened
  // it fresh and picked this creator there themselves, not some separate/looser filtering. A new
  // tab (not an in-app navigation) can't carry Angular Router navigation `state` - that only
  // survives within the same browser tab/history - so the creator id crosses via a query param
  // instead, which CampaignDetailsComponent reads once at construction to seed its initial
  // campaignAnalyticsFilter().creatorIds (see setInitialFilters there).
  public onCampaignClicked(
    row: ICampaignDetailsDeliverablesGroupedParentRow | ICampaignDetailsDeliverablesTableRow,
  ): void {
    if (!row.campaignId) {
      return;
    }

    const campaignRoute = this.adminMode() ? '/admin/campaigns' : '/campaigns';
    const url = this.router.serializeUrl(
      this.router.createUrlTree([campaignRoute, row.campaignId], { queryParams: { creatorId: this.creatorId() } }),
    );

    WindowRedirectService.openUrlInNewTab(url);
  }

  public onTileClick(tileConfiguration: ITileConfiguration, campaignId?: string): void {
    this.openDialogForNode(tileConfiguration.id, campaignId, {
      selectedCtaId: tileConfiguration.selectedCtaId ?? null,
    });
  }

  // Needs the creator, since a persisted "Full period" is resolved from its createdAt.
  private initializeDateRangeEffect(): void {
    effect(() => {
      const creatorId = this.creatorId();

      if (!creatorId || !this.creator() || untracked(this.dateFilterState)) {
        return;
      }

      untracked(() => this.initializeDateRange(creatorId));
    });
  }

  private initializeDateRange(creatorId: string): void {
    const fullPeriodBounds = this.fullPeriodBounds();
    const last30Days = calculatePredefinedDateRange(EDateRangeType.last30Days, null);
    const resolvedState = this.profileDateFilterStateService.resolveState({
      defaultEndDate: last30Days.end,
      defaultSelectedRange: EDateRangeType.last30Days,
      defaultStartDate: last30Days.start,
      entityId: creatorId,
      entityType: 'creatorDeliverables',
      // Without a createdAt, a persisted "Full period" can't be resolved - falls back to "Last 30 days".
      hideAllTimeRangeOption: !fullPeriodBounds,
      userId: AuthService.currentDbUser?.id ?? null,
    });
    const { selectedRange } = resolvedState;
    // "Full period" ends today, not on the day it was persisted.
    const { end, start } =
      selectedRange === EDateRangeType.allTime
        ? calculatePredefinedDateRange(selectedRange, fullPeriodBounds)
        : { end: resolvedState.endDate, start: resolvedState.startDate };

    this.selectedDateRangeType.set(selectedRange);
    this.startDate.set(start);
    this.endDate.set(end);
    this.dateFilterState.set({ endDate: end, selectedRange, startDate: start });
  }

  private openDialogForNode(
    analyticsNodeId: string,
    campaignId: string | null | undefined,
    extra: Pick<
      ICtaAnalyticsDialogData,
      'selectedCtaId' | 'selectedDeliverableId' | 'selectedDeliverableType' | 'viewMode'
    >,
  ): void {
    const analyticsNode = this.store
      .analyticsNodes$()
      ?.find((node) => node.id === analyticsNodeId && (!campaignId || node.campaignId === campaignId));

    if (!analyticsNode) {
      return;
    }

    // Resolved from the analyticsNode's OWN campaignId (always real, never the merged scope
    // shell) - critical when re-navigating inside the dialog to a tile that may belong to a
    // different campaign than the row that originally opened it.
    const campaignAnalytics: ICampaignAnalytics | undefined =
      this.store.campaignAnalyticsById$()[analyticsNode.campaignId];
    const campaign = this.store.campaigns$().find((c) => c.id === analyticsNode.campaignId);

    if (!campaignAnalytics || !campaign) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode(),
      analyticsNode,
      campaign,
      campaignAnalytics,
      campaignExport: null!,
      creatorBriefingMode: false,
      currentTab: toEventFrontendTab(ECampaignDetailsTab.deliverables),
      exportMode: false,
      ...extra,
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  private openCtaAnalyticsDialog(dialogData: ICtaAnalyticsDialogData): void {
    const dialogRef = this.dialog.open(CtaAnalyticsDialogComponent, {
      data: dialogData,
      maxWidth: '1800px',
      width: '90%',
    });

    dialogRef.afterClosed().subscribe((result: ITileConfiguration | undefined) => {
      if (result) {
        this.onTileClick(result, dialogData.campaign.id);
      }
    });
  }

  private setsAreEqual<T>(left: Set<T>, right: Set<T>): boolean {
    if (left.size !== right.size) {
      return false;
    }

    for (const value of left) {
      if (!right.has(value)) {
        return false;
      }
    }

    return true;
  }
}
